import React from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";

const PROFILE_URL = "https://de.trustpilot.com/review/tobinunner.de";
const EVALUATE_URL = "https://de.trustpilot.com/evaluate/tobinunner.de";

// Echte Trustpilot-Bewertungen (Stand Okt. 2025) — Quelle: Trustpilot-Profil, 4,5 / 16 Bewertungen.
const REVIEWS = [
  {
    title: "Reibungsloser Ablauf",
    date: "Okt. 2025",
    url: "https://de.trustpilot.com/reviews/68edda28a76a41a6e3983742",
    text:
      "Die Coaching-Erfahrung mit Tobi lief in jedem Aspekt makellos. Vom Bestellvorgang bis zur Erstellung des Trainingsplans lief alles transparent und problemlos. Die Betreuung hat mir geholfen, ein gesünderes Leben umzusetzen und meine Leistungen deutlich zu steigern. Stark weiterzuempfehlen.",
  },
  {
    title: "Absolut empfehlenswert",
    date: "Okt. 2025",
    url: "https://de.trustpilot.com/reviews/68ecc6a6a202aeb1a6572019",
    text:
      "Tobi hat das Coaching individuell gestaltet und ist zu jeder Zeit auf meine Fragen eingegangen. Die Trainings- und Ernährungspläne sind professionell und strukturiert. In den 6 Monaten Zusammenarbeit ist alles ideal verlaufen und ich komme meinem Ziel immer näher!",
  },
  {
    title: "Super Coaching",
    date: "Okt. 2025",
    url: "https://de.trustpilot.com/reviews/68eca98dd023069e41368f40",
    text:
      "Klare Struktur und individuell angepasste Trainings- und Ernährungspläne. Professionelle und zuverlässige Betreuung mit regelmäßigem Feedback. Fortschritte sind schnell sichtbar – ein sehr durchdachtes und effektives Konzept!",
  },
  {
    title: "Sehr erfahrener Coach",
    date: "Okt. 2025",
    url: "https://de.trustpilot.com/reviews/68eca0adc1529ff6ab0d811e",
    text:
      "Er konnte mir einen individuellen Trainingsplan erstellen, der perfekt zu meinem Alltag passt, sodass ich große Fortschritte in Fitness und Gesundheit machen konnte. Durch sein Kochbuch lernt man einfach und kostengünstig zu kochen. Klare Empfehlung!",
  },
  {
    title: "Bis jetzt das beste Coaching",
    date: "Okt. 2025",
    url: "https://de.trustpilot.com/reviews/68ec9a9a1019e4ce6e1aace8",
    text:
      "Bis jetzt das beste Coaching, das ich online jemals gekauft habe. Super realitätsnah, schnelle Fortschritte ohne große Umstellung. Habe Gesundheit mittlerweile wirklich zum Lifestyle gemacht.",
  },
  {
    title: "Super Kochbuch",
    date: "Okt. 2025",
    url: "https://de.trustpilot.com/reviews/68ef7966cb6cf328cffef2b8",
    text:
      "Super Kochbuch, vielfältige Rezepte, sehr zu empfehlen – vor allem für den fairen Preis.",
  },
];

const Stars = ({ count = 5, size = 20 }) => (
  <div className="flex gap-0.5" aria-label={`${count} von 5 Sternen`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <span
        key={i}
        className={`inline-flex items-center justify-center rounded-[3px] ${i < count ? "bg-[#00B67A]" : "bg-[#5A6648]"}`}
        style={{ width: size + 6, height: size + 6 }}
      >
        <Star size={size - 4} className="text-white fill-white" />
      </span>
    ))}
  </div>
);

const TrustpilotLogo = () => (
  <span className="inline-flex items-center gap-1.5 font-bold text-[#F3F1E8]">
    <Star size={18} className="text-[#00B67A] fill-[#00B67A]" />
    <span className="tracking-tight">Trustpilot</span>
  </span>
);

const TrustpilotReviews = () => {
  return (
    <section
      id="trustpilot-reviews"
      className="relative py-16 md:py-24 border-t border-[#556340]"
      data-testid="trustpilot-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header / Aggregate */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55 }}
          className="flex flex-col items-center text-center"
        >
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0] mb-4">
            Das sagen Klienten
          </div>
          <div className="flex flex-col items-center gap-3">
            <TrustpilotLogo />
            <Stars count={5} size={22} />
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl font-black text-[#F3F1E8]" data-testid="trustpilot-score">
                4,5
              </span>
              <span className="text-[#C9C6B4] text-sm">von 5 · Hervorragend</span>
            </div>
            <a
              href={PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-[#C9C6B4] hover:text-[#F3F1E8] underline underline-offset-4 inline-flex items-center gap-1.5"
              data-testid="trustpilot-count-link"
            >
              Basierend auf 16 echten Bewertungen auf Trustpilot
              <ExternalLink size={13} />
            </a>
          </div>
        </motion.div>

        {/* Review cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="trustpilot-reviews-grid">
          {REVIEWS.map((r, idx) => (
            <motion.a
              key={r.url}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: idx * 0.07 }}
              whileHover={{ y: -4 }}
              className="group flex flex-col rounded-2xl border border-[rgba(255,255,255,0.14)] bg-[#4E5A3B]/60 hover:bg-[#4E5A3B]/80 hover:border-[#E8E5D0]/40 transition-colors p-6"
              data-testid={`trustpilot-review-${idx}`}
            >
              <Stars count={5} size={16} />
              <h3 className="mt-4 font-display font-black text-lg text-[#F3F1E8] tracking-tight">
                {r.title}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[#E5E2D0] flex-1">
                {r.text}
              </p>
              <div className="mt-5 pt-4 border-t border-[#556340] flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[#00B67A] font-bold">
                  <Star size={12} className="fill-[#00B67A]" /> Verifiziert
                </span>
                <span className="text-[11px] uppercase tracking-[0.16em] text-[#8F8C7A]">{r.date}</span>
              </div>
            </motion.a>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider px-6 py-3.5 rounded-full transition-colors"
            data-testid="trustpilot-all-btn"
          >
            Alle Bewertungen ansehen <ExternalLink size={16} />
          </a>
          <a
            href={EVALUATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[rgba(255,255,255,0.28)] hover:bg-black/[0.08] text-[#F3F1E8] font-bold rounded-full px-6 py-3.5 transition-colors"
            data-testid="trustpilot-evaluate-btn"
          >
            <Star size={16} className="text-[#00B67A] fill-[#00B67A]" /> Bewertung abgeben
          </a>
        </div>
      </div>
    </section>
  );
};

export default TrustpilotReviews;
